import { useQuery, useQueryClient } from '@tanstack/react-query'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { ESKResponse, GetContentModuleResponse, MSKResponse, TopicResponse } from '@/services/api/api.types'
import { deduplicateByName } from '@/utils/sidekicks'

import { getSidekickId } from './utils'

export type SidekickOption = MSKResponse &
  ESKResponse & {
    isAvailable: boolean // false if taken as MSK, assigned to a topic, or assigned to a content module
    isSelected: boolean // true if currently chosen as MSK for this sponsor
    isFromPool?: boolean // Track if this sidekick came from the pool
  }

const mskToSidekickOption = (
  msk: MSKResponse,
  isAvailable: boolean,
  isSelected: boolean = true,
  isFromPool: boolean = false,
): SidekickOption => {
  return {
    ...msk,
    isAvailable,
    isSelected,
    isFromPool,
    topicId: null,
    type: msk.id ?? '', // Use id as type for MSKs
    introduction: '',
    systemPrompt: '',
    allowedForSwitchCm: false,
    additionalInformation: '',
    category: '',
    expertise: '',
    notRemovable: false,
  }
}

const eskToSidekickOption = (
  esk: ESKResponse,
  isAvailable: boolean,
  isSelected: boolean,
  isFromPool: boolean,
): SidekickOption => {
  return {
    ...esk,
    id: esk.type,
    _id: esk.type,
    isAvailable,
    isSelected,
    isFromPool,
    default: false,
    isRequired: false,
  }
}

const getDefaultSidekick = (mskTypes: MSKResponse[]): string | null => {
  return mskTypes.find(msk => msk.default)?.id ?? null
}

const sidekickOptionToMsk = (sidekick: SidekickOption): MSKResponse => {
  // Only nullify IDs for new sidekicks from pool that are being newly added
  // Existing MSKs (isFromPool: false) should keep their original IDs
  const isNewFromPool = sidekick.isFromPool === true
  return {
    id: isNewFromPool ? undefined : sidekick.id,
    _id: isNewFromPool ? undefined : sidekick._id,
    default: sidekick.default,
    name: sidekick.name,
    ttsId: sidekick.ttsId,
    createdBy: sidekick.createdBy,
    sponsorId: sidekick.sponsorId,
    active: sidekick.active,
    createdAt: sidekick.createdAt,
    avatarId: sidekick.avatarId,
    avatarImage: sidekick.avatarImage,
    avatarRiveFile: sidekick.avatarRiveFile,
    pitch: sidekick.pitch,
    voiceSpeed: sidekick.voiceSpeed,
    gender: sidekick.gender,
    isRequired: sidekick.isRequired,
  }
}

const sidekickOptionToEsk = (sidekick: SidekickOption): ESKResponse => {
  // Remove the MSK-specific properties
  const {
    id: _id,
    _id: _innerId,
    default: _defaultMsk,
    isFromPool: _isFromPool,
    isSelected: _isSelected,
    ...esk
  } = sidekick
  return esk
}

// Extract the base processing logic (expensive operations that don't depend on topics)
const processBaseSidekickData = (
  currentSidekicksData: { mskTypes: MSKResponse[]; eskTypes: ESKResponse[] },
  poolSidekicksData: { mskTypes: MSKResponse[]; eskTypes: ESKResponse[] },
  sponsorId: string,
) => {
  const currentMsks = currentSidekicksData?.mskTypes ?? []
  const currentEsks = currentSidekicksData?.eskTypes ?? []
  const poolMsks = poolSidekicksData?.mskTypes ?? []
  const poolEsks = poolSidekicksData?.eskTypes ?? []

  // Get all current sidekick names for comparison
  const currentMskNames = new Set(currentMsks.map(msk => msk.name))
  const currentEskNames = new Set(currentEsks.map(esk => esk.name))
  const allCurrentNames = new Set([...currentMskNames, ...currentEskNames])

  // Convert current MSKs to SidekickOption format (selected as MSK, therefore not available for ESK use)
  const currentMskOptions = currentMsks.map(msk => {
    return mskToSidekickOption(msk, false, true, false) // MSKs are never available (taken as MSKs)
  })

  // Convert current ESKs to SidekickOption format (availability will be calculated separately)
  const currentEskOptions = currentEsks.map(esk => {
    return eskToSidekickOption(esk, true, false, false) // Default to available, will be updated based on topics
  })

  // Get pool sidekicks that aren't already in use by current sponsor (determined by name)
  const availablePoolMsks = poolMsks.filter(msk => !allCurrentNames.has(msk.name))
  const availablePoolEsks = poolEsks.filter(esk => !allCurrentNames.has(esk.name))

  // Deduplicate between MSK and ESK pools - if a name appears in both, prefer MSK
  const poolMskNames = new Set(availablePoolMsks.map(msk => msk.name))
  const deduplicatedPoolEsks = availablePoolEsks.filter(esk => {
    if (poolMskNames.has(esk.name)) {
      console.warn(`Duplicate sidekick name "${esk.name}" found in both MSK and ESK pools. Using MSK version.`)
      return false
    }
    return true
  })

  // Convert available pool sidekicks to SidekickOption format (not selected, from pool)
  const availablePoolMskOptions = availablePoolMsks.map(msk => {
    return mskToSidekickOption({ ...msk, sponsorId }, true, false, true) // Pool MSKs are available (not taken as MSKs yet)
  })
  const availablePoolEskOptions = deduplicatedPoolEsks.map(esk => {
    return eskToSidekickOption({ ...esk, sponsorId, topicId: null }, true, false, true) // Default to available
  })

  const currentSidekicks = [...currentMskOptions, ...currentEskOptions]
  const poolSidekicks = [...availablePoolMskOptions, ...availablePoolEskOptions]

  // Combine all sidekicks for the complete list
  const combinedSidekicks = [...currentSidekicks, ...poolSidekicks]

  // Deduplicate by name: prefer sponsor-in-use (!isFromPool), then tiebreak by smaller id
  const uniqueByNameSidekicks = deduplicateByName(combinedSidekicks, {
    getName: s => s.name ?? '',
    getId: s => s.id ?? '',
    getPriority: s => (s.isFromPool === false ? 1 : 0),
  })

  // Check for duplicate IDs and resolve them (immutably)
  const seenIds = new Set<string>()
  const allSidekicks = uniqueByNameSidekicks.map(sidekick => {
    if (sidekick.id && seenIds.has(sidekick.id)) {
      console.warn(
        `Duplicate sidekick id "${sidekick.id}" found in allSidekicks. Generating new uuid for sidekick (${sidekick.name}).`,
      )
      const newId = nanoid()
      const deduplicatedSidekick = {
        ...sidekick,
        id: newId,
        _id: newId,
        type: newId,
      }
      seenIds.add(newId)
      return deduplicatedSidekick
    }

    if (sidekick.id) {
      seenIds.add(sidekick.id)
    }
    return sidekick
  })

  // Get default sidekick
  const defaultMsk = getDefaultSidekick(currentMsks)

  return {
    allSidekicks,
    selectedMsks: currentMskOptions,
    defaultMsk,
  }
}

// Fast function to update availability based on topic and content module assignments
const updateSidekickAvailability = (
  baseSidekicks: SidekickOption[],
  topics: TopicResponse[],
  contentModules: GetContentModuleResponse[],
): SidekickOption[] => {
  const topicAssignedIds = topics.map(topic => topic.skId).filter(Boolean)
  const cmAssignedIds = contentModules
    .map(cm => cm.selectEsk)
    .filter((v): v is string => v != null && v !== '' && v !== 'PSK')

  const assignedEskIds = new Set([...topicAssignedIds, ...cmAssignedIds])

  return baseSidekicks.map(sidekick => ({
    ...sidekick,
    isAvailable: sidekick.isSelected ? false : !assignedEskIds.has(getSidekickId(sidekick)),
  }))
}

export function useSidekicks() {
  const { api, authenticationStore } = useGlobalState()
  const { user } = authenticationStore
  const queryClient = useQueryClient()

  const sidekicksQuery = useQuery({
    queryKey: ['sponsor', 'sidekicks'],
    queryFn: api.getSidekicks,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const sidekickPoolQuery = useQuery({
    queryKey: ['sponsor', 'sidekick-pool'],
    queryFn: api.getSidekickPool,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: api.getSponsorTopics,
    initialData: [],
    refetchOnMount: 'always',
    staleTime: 0,
  })

  // Memoize expensive base processing (only runs when sidekick data changes)
  const baseSidekickData = useMemo(() => {
    const sponsorId = user?.sponsorId ?? ''
    return processBaseSidekickData(
      sidekicksQuery.data || { mskTypes: [], eskTypes: [] },
      sidekickPoolQuery.data || { mskTypes: [], eskTypes: [] },
      sponsorId,
    )
  }, [sidekicksQuery.data, sidekickPoolQuery.data, user?.sponsorId])

  const contentModulesQuery = useQuery({
    queryKey: ['sponsor', 'content-modules'],
    queryFn: () => api.getContentModules(),
    initialData: [],
    refetchOnMount: 'always',
    staleTime: 0,
  })

  // Fast availability update (runs when topics or content modules change)
  const allSidekicks = useMemo(() => {
    return updateSidekickAvailability(
      baseSidekickData.allSidekicks,
      topicsQuery.data || [],
      contentModulesQuery.data || [],
    )
  }, [baseSidekickData.allSidekicks, topicsQuery.data, contentModulesQuery.data])

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sponsor', 'sidekicks'] })
    await queryClient.invalidateQueries({ queryKey: ['sponsor', 'topics'] })
    await queryClient.invalidateQueries({ queryKey: ['sponsor', 'content-modules'] })
  }

  return {
    allSidekicks,
    selectedMsks: baseSidekickData.selectedMsks,
    defaultMsk: baseSidekickData.defaultMsk,
    isLoading:
      sidekicksQuery.isLoading || sidekickPoolQuery.isLoading || topicsQuery.isLoading || contentModulesQuery.isLoading,
    error: sidekicksQuery.error || sidekickPoolQuery.error || topicsQuery.error || contentModulesQuery.error,
    // Utility functions
    sidekickOptionToMsk,
    sidekickOptionToEsk,
    invalidate,
  }
}
